import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { getCoupons, getCoupon, createCoupon, updateCoupon, deleteCoupon, archiveCoupon, getCouponCode } from './api';
import { CouponResponse, Coupon, CreateCouponRequest, UpdateCouponRequest, CouponCodeResponse } from './types';
import { useParams } from 'react-router-dom';

export function useGetCoupons(params?: Record<string, any>, options?: UseQueryOptions<CouponResponse, Error>) {
  return useQuery<CouponResponse>({
    queryKey: ['coupons', params],
    queryFn: () => getCoupons(params),
    ...options
  });
}

export function useGetCouponCode(generateCode: boolean, options?: UseQueryOptions<CouponCodeResponse, Error>) {
  return useQuery<CouponCodeResponse>({
    queryKey: ['coupon_code'],
    queryFn: () => getCouponCode(),
    enabled: generateCode,
    ...options
  });
}

export function useGetCoupon(options?: UseQueryOptions<Coupon, Error>) {
  const { couponId } = useParams();

  return useQuery<Coupon>({
    queryKey: ['coupon', couponId],
    queryFn: () => getCoupon(couponId!),
    enabled: Boolean(couponId),
    ...options
  });
}

export function useCreateCoupon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCouponRequest) => createCoupon(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
    }
  });
}

export function useUpdateCoupon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateCouponRequest) => updateCoupon(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
      queryClient.invalidateQueries({ queryKey: ['coupon', data.id] });
    }
  });
}

export function useArchiveCoupon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { id: number; status: 'active' | 'archived' }) => archiveCoupon(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
      queryClient.invalidateQueries({ queryKey: ['coupon', data.id] });
    }
  });
}

export function useDeleteCoupon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (couponId: number) => deleteCoupon(couponId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
    }
  });
}
